import { Col, Space, Table, Popover, Checkbox, Input } from 'antd'
import { ButtonActionBar, Button as CustomButton, Typography } from 'modules/core/components'
import { LeftCircleOutlined, SettingOutlined, DownloadOutlined } from '@ant-design/icons'
import { useTranslation } from 'react-i18next'
import { useMemo, useState, useEffect } from 'react'
import numeral from 'numeral'
import { calculateScrollX } from 'modules/core/utils'
import _ from 'lodash'

const isAmountKey = (key) => key === 'amount' || /^\d{4}-\d{2}$/.test(key)

const isMonthColumn = (key) => /^\d{4}-\d{2}$/.test(key)

const sortColumns = (columns, columnsWithData) => {
  return [...columns].sort((a, b) => {
    const aHasData = columnsWithData.includes(a)
    const bHasData = columnsWithData.includes(b)

    const aIsMonth = isMonthColumn(a)
    const bIsMonth = isMonthColumn(b)

    const aIsAmount = a === 'amount'
    const bIsAmount = b === 'amount'

    // 1. Dimensiones primero
    const aIsDimension = !aIsMonth && !aIsAmount
    const bIsDimension = !bIsMonth && !bIsAmount

    if (aIsDimension && !bIsDimension) return -1
    if (!aIsDimension && bIsDimension) return 1

    // 2. Amount después de dimensiones
    if (aIsAmount && !bIsAmount && !bIsMonth) return 1
    if (!aIsAmount && bIsAmount && !aIsMonth) return -1

    // 3. Meses siempre al final
    if (aIsMonth && !bIsMonth) return 1
    if (!aIsMonth && bIsMonth) return -1

    // 4. Meses ordenados cronológicamente
    if (aIsMonth && bIsMonth) {
      return new Date(`${a}-01`) - new Date(`${b}-01`)
    }

    // 5. Dentro del mismo grupo, con datos primero
    if (aHasData && !bHasData) return -1
    if (!aHasData && bHasData) return 1

    return 0
  })
}

const GlobalBudgetSACDetail = ({ applicationDetail, detailLoading, onDownload, onReturn }) => {
  const [downloading, setDownloading] = useState(false)
  const [visibleColumns, setVisibleColumns] = useState([])
  const [columnSearch, setColumnSearch] = useState('')
  const [columnsInitialized, setColumnsInitialized] = useState(false)
  const { t } = useTranslation()

  const allColumns = useMemo(() => {
    if (!applicationDetail?.length) return []

    return _.uniq(applicationDetail.flatMap((item) => Object.keys(item)))
  }, [applicationDetail])

  const columnsWithData = useMemo(() => {
    return allColumns.filter((column) =>
      applicationDetail.some((item) => {
        const value = item[column]

        return value !== null && value !== undefined && value !== ''
      }),
    )
  }, [applicationDetail, allColumns])

  useEffect(() => {
    if (!columnsInitialized && columnsWithData.length > 0) {
      setVisibleColumns(columnsWithData)
      setColumnsInitialized(true)
    }
  }, [columnsWithData, columnsInitialized])

  useEffect(() => {
    setColumnsInitialized(false)
  }, [applicationDetail])

  const columns = useMemo(() => {
    return sortColumns(visibleColumns, columnsWithData).map((key) => ({
      dataIndex: key,
      title: key.replace(/_/g, ' ').toUpperCase(),

      width: 180,

      ellipsis: true,

      ...(isAmountKey(key) && {
        align: 'right',

        render: (value) =>
          value === null || value === undefined || value === ''
            ? value
            : numeral(value).format('0,0.00'),
      }),
    }))
  }, [visibleColumns, columnsWithData])

  const filteredColumns = useMemo(() => {
    return allColumns.filter((column) => column.toLowerCase().includes(columnSearch.toLowerCase()))
  }, [allColumns, columnSearch])

  const columnSelector = (
    <div
      style={{
        width: 300,
      }}
    >
      <Input.Search
        placeholder="Buscar columna..."
        allowClear
        value={columnSearch}
        onChange={(e) => setColumnSearch(e.target.value)}
        style={{
          marginBottom: 12,
        }}
      />

      <ButtonActionBar position="space-between">
        <a onClick={() => setVisibleColumns(allColumns)}>Mostrar todas</a>

        <a onClick={() => setVisibleColumns(columnsWithData)}>Solo con datos</a>
      </ButtonActionBar>

      <div
        style={{
          maxHeight: 300,
          overflowY: 'auto',
          borderTop: '1px solid #f0f0f0',
          paddingTop: 8,
        }}
      >
        <Checkbox.Group value={visibleColumns} onChange={(values) => setVisibleColumns(values)}>
          <Space
            direction="vertical"
            style={{
              width: '100%',
            }}
          >
            {sortColumns(filteredColumns, columnsWithData).map((column) => (
              <Checkbox key={column} value={column}>
                {column.replace(/_/g, ' ').toUpperCase()}

                {!columnsWithData.includes(column) && (
                  <span
                    style={{
                      color: '#999',
                      marginLeft: 8,
                      fontSize: 12,
                    }}
                  >
                    (sin datos)
                  </span>
                )}
              </Checkbox>
            ))}
          </Space>
        </Checkbox.Group>
      </div>
    </div>
  )

  return (
    <>
      <Col span={24}>
        <Space direction="horizontal">
          <CustomButton.Icon
            title="ACTION_RETURN"
            icon={<LeftCircleOutlined style={{ fontSize: 20, color: '#0047ba' }} />}
            onClick={() => onReturn()}
          />
          <Typography.Body>{t('PLANNING_SAC_RETURN_APPLICATIONS_LIST_TYPOGRAPHY')}</Typography.Body>
        </Space>
      </Col>
      <Col
        span={24}
        style={{
          marginTop: 16,
          marginBottom: 16,
        }}
      >
        <Space direction="horizontal">
          <Popover content={columnSelector} trigger="click" placement="bottomLeft">
            <div>
              <CustomButton.Default icon={<SettingOutlined />} title="Personalizar columnas" />
            </div>
          </Popover>
          <CustomButton.Icon
            icon={<Typography.Icon icon={DownloadOutlined} />}
            title="ADJUSTMENT_DOWNLOAD_MOVEMENT_ACTION_TITLE"
            loading={downloading}
            disabled={!applicationDetail?.length}
            onClick={() => {
              setDownloading(true)
              onDownload()
                .catch(() => {})
                .finally(() => setDownloading(false))
            }}
          />
        </Space>
      </Col>
      <Col span={24}>
        <Table
          size="small"
          columns={columns}
          loading={detailLoading}
          dataSource={applicationDetail}
          rowKey={(record, index) => record.id || index}
          pagination={{
            pageSize: 10,
            showSizeChanger: true,
          }}
          scroll={{
            x: calculateScrollX(columns),
          }}
        />
      </Col>
    </>
  )
}

export default GlobalBudgetSACDetail
